import React, { useState, useRef, useCallback } from 'react';
import AnimationforWork from '../animations/AnimationforWork';
import MouseAnimation from '../animations/MouseAnimation';
import useScrollReveal from '../../hooks/useScrollReveal';
import './Work.css';

const Work = ({ isDarkMode }) => {
  const [expandedItems, setExpandedItems] = useState({});
  const touchStartRef = useRef({ x: 0, y: 0, time: 0 });
  const wasTouchEventRef = useRef(false);
  const containerRef = useRef(null);

  useScrollReveal(containerRef);

  const toggleItemExpansion = (index) => {
    setExpandedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };

  const isTap = useCallback((touchEndEvent) => {
    const touch = touchEndEvent.changedTouches[0];
    const dx = Math.abs(touch.clientX - touchStartRef.current.x);
    const dy = Math.abs(touch.clientY - touchStartRef.current.y);
    const dt = Date.now() - touchStartRef.current.time;
    return dx < 10 && dy < 10 && dt < 300;
  }, []);

  const handleTouchStart = useCallback((e) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY, time: Date.now() };
  }, []);

  const handleCardTouchEnd = useCallback((index, e) => {
    wasTouchEventRef.current = true;
    if (isTap(e)) {
      toggleItemExpansion(index);
    }
  }, [isTap]);

  const handleLinkTouchEnd = useCallback((link, e) => {
    e.stopPropagation();
    wasTouchEventRef.current = true;
    if (isTap(e)) {
      window.open(link, '_blank');
    }
  }, [isTap]);

  const handleCardClick = useCallback((index) => {
    if (wasTouchEventRef.current) {
      wasTouchEventRef.current = false;
      return;
    }
    toggleItemExpansion(index);
  }, []);

  const workItems = [
    {
      link: 'https://www.wellsfargo.com/',
      logo: '/WellsFargo.png',
      position: 'Software Engineer',
      techStack: ['Python', 'Java', 'Kafka', 'MongoDB', 'Splunk', 'Jira'],
      description: [
        <>Built an automated change-detection workflow in <strong>Python</strong> for large-scale datasets using <strong>Jira and GitHub MCPs</strong> (Model Context Protocols) to update documentation, cutting manual effort and review time by more than <strong>90%</strong>.</>,
        <>Developed a <strong>data-ingestion pipeline</strong> that processed and stored documents, integrating <strong>Tachyon Semantic Search and Chat Completion APIs</strong>, which automated content management and reduced manual handling.</>,
        <>Designed <strong>data contracts</strong> and API specifications that linked machine-learning models to internal services, enabling consistent data exchange and lowering integration errors across multiple teams and downstream consumers.</>,
        <>Upgraded legacy APIs in <strong>Java</strong> to use <strong>Kafka</strong> streaming; added <strong>BDD/JUnit</strong> tests and <strong>Splunk</strong> monitoring, which increased API reliability and shortened issue detection time for faster incident response and recovery.</>,
        <>Co-led the migration of databases from <strong>SQL</strong> to <strong>MongoDB</strong>, supporting the transition from a monolithic architecture to microservices and improving system scalability while reducing schema rigidity in production.</>,
        <>Advised on architecture for an <strong>ML-enabled pre-decision layer</strong> in the Small Business Lending journey, coordinating asynchronous credit-check processing with front-end state management to maintain user engagement.</>
      ],
      companyName: 'Wells Fargo',
      featured: true,
    },
    {
      link: 'https://www.americanexpress.com/',
      logo: '/AmericanExpress.png',
      position: 'Software Engineer Intern',
      techStack: ['React', 'Node.js', 'Cassandra', 'Docker', 'OAuth'],
      description: [
        <>Led intern team to develop a <strong>full-stack web application</strong> using <strong>React JS, Node JS</strong>, and <strong>Cassandra</strong> introducing <strong>automated data visualization</strong> for Network Modernization Team.</>,
        <>Containerized the application with <strong>Docker</strong> and added <strong>SSO authentication using OAuth</strong>, which made application deployable and strengthened enterprise security.</>,
        <>Built <strong>RESTful APIs</strong> and optimized backend query flows for faster dashboard refresh, enabling near real-time reporting and reducing turnaround time for stakeholder updates.</>
      ],
      companyName: 'American Express',
      featured: true,
    },
    {
      link: 'https://www.blueverse.club/',
      logo: '/Blueverse.png',
      position: 'Software Engineer Intern',
      techStack: ['React Native', 'iOS', 'Android'],
      description: [
        <>Developed BV Business app for iOS & Android in <strong>React Native</strong>, boosting business user engagement by <strong>78%</strong>.</>,
        <>Enhanced BlueVerse User app by reducing load time by <strong>22%</strong>, achieving a <strong>5.0 rating</strong> on App Store.</>,
        <>Utilized agile methodologies and project management tools including <strong>Jira, Confluence, and Notion</strong> for PM.</>,
        <>Collaborated with cross-functional teams to design architecture, meeting all performance targets.</>
      ],
      companyName: 'BlueVerse Inc.'
    },
    {
      link: 'https://www.ttu.edu/',
      logo: '/texas.png',
      position: 'Tutor',
      techStack: ['CS Fundamentals', 'Mentoring'],
      description: [
        <>Provided personalized tutoring to over <strong>50+ students</strong>, earning <strong>'Tutor of the Semester'</strong> award.</>,
        <>Tutored students to master foundational <strong>CS principles</strong>, which improved their overall academic performance.</>
      ],
      companyName: 'Texas Tech University'
    },
    {
      link: 'https://hackwestx-fall-2023.devpost.com/',
      logo: '/HackWesTX.png',
      position: 'Database Mentor, HackWesTX',
      techStack: ['Databases', 'Git', 'Web Dev'],
      description: [
        <>Mentored participants on <strong>database technologies, web development</strong>, & <strong>Git</strong> collaboration at HackWesTX hackathon.</>,
        <>Received <strong>'Best Mentor'</strong> award for Fall '23 in recognition of effective guidance and instruction.</>
      ],
      companyName: 'HackWesTX'
    },
    {
      link: 'https://www.ttu.edu/',
      logo: '/texas.png',
      position: 'Student Assistant',
      techStack: ['Operations', 'Data Entry'],
      description: [
        <>Assisted students with housing-related concerns, contributing to smoother housing operations and improved student experience.</>,
        <>Managed package check-ins to ensure accurate tracking and minimize data loss.</>
      ],
      companyName: 'Texas Tech University'
    },
    {
      link: 'https://www.chick-fil-a.com/',
      logo: '/cfa.png',
      position: 'Team Member',
      techStack: ['Teamwork', 'Operations'],
      description: [
        <>Worked as a Team Member and gained strong collaboration and teamwork experience.</>,
        <>Handled all kitchen operations, improving understanding of back-end restaurant workflows and time-sensitive task execution.</>
      ],
      companyName: 'Chick-fil-A Restaurants'
    }
  ];

  return (
    <>
      <MouseAnimation isDarkMode={isDarkMode} />
      <div className="spacery4">
        <div className='headding'>
          <AnimationforWork isDarkMode={isDarkMode} />
          <div className="textDiv">
            <h1>Work</h1>
          </div>
        </div>
      </div>

      <div ref={containerRef} className="container work-container bento-grid">
        {workItems.map((item, index) => (
          <div
            key={index}
            className={`card bento-card ${expandedItems[index] ? 'active' : ''} ${item.featured ? 'featured' : ''}`}
            data-delay={index + 1}
            onClick={() => handleCardClick(index)}
            onTouchStart={handleTouchStart}
            onTouchEnd={(e) => handleCardTouchEnd(index, e)}
          >
            {/* Terminal overlay on hover */}
            <div className="terminal-overlay">
              <div className="terminal-header">
                <span className="terminal-dot red" />
                <span className="terminal-dot yellow" />
                <span className="terminal-dot green" />
                <span className="terminal-title">~/tech-stack</span>
              </div>
              <div className="terminal-body">
                {item.techStack.map((tech, i) => (
                  <div key={i} className="terminal-line">
                    <span className="terminal-prompt">$</span> {tech}
                  </div>
                ))}
              </div>
            </div>

            <svg
              className="with-icon_icon__MHUeb link-icon"
              onClick={(e) => { e.stopPropagation(); window.open(item.link, '_blank'); }}
              onTouchEnd={(e) => handleLinkTouchEnd(item.link, e)}
              data-testid="geist-icon"
              fill="none"
              height="24"
              shapeRendering="geometricPrecision"
              stroke="currentColor"
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="1.5"
              viewBox="0 0 24 24"
              width="24"
            >
              <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
              <path d="M15 3h6v6" />
              <path d="M10 14L21 3" />
            </svg>

            <div className="logo-container">
              <img
                src={isDarkMode ? item.logo.replace('.png', '.png') : item.logo}
                alt={item.companyName}
                className="company-logo"
              />
            </div>

            <div className="info-container">
              <div className="position">
                <strong>{item.position}</strong> &nbsp;
                <svg
                  className="with-icon_icon__MHUeb link-icon2"
                  onClick={(e) => { e.stopPropagation(); window.open(item.link, '_blank'); }}
                  onTouchEnd={(e) => handleLinkTouchEnd(item.link, e)}
                  data-testid="geist-icon"
                  fill="none"
                  height="24"
                  shapeRendering="geometricPrecision"
                  stroke="currentColor"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="1.5"
                  viewBox="0 0 24 24"
                  width="24"
                >
                  <path d="M18 13v6a2 2 0 01-2 2H5a2 2 0 01-2-2V8a2 2 0 012-2h6" />
                  <path d="M15 3h6v6" />
                  <path d="M10 14L21 3" />
                </svg>
              </div>

              <ul className={`company-description ${expandedItems[index] ? 'active' : ''}`}>
                {item.description.map((line, i) => (
                  <li key={i}>{line}</li>
                ))}
              </ul>

              <div className={`company-name forwork ${expandedItems[index] ? 'active' : ''}`}>
                {item.companyName}
              </div>

              <div className="expand-indicator">
                <svg
                  className={`chevron-icon ${expandedItems[index] ? 'rotated' : ''}`}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  width="20"
                  height="20"
                >
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="spacery456"></div>
    </>
  );
};

export default Work;
